/** Copyright © 2013-2022 DataYes, All Rights Reserved. */

import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import user from 'utils/user';
import { MenuItem } from './types';
import { filterMenu, flattenMenu, formatMenuItem, getPath } from './helper';
import { MENU } from './configs';
import { StyledMenu } from './style';

export default function MenuList(): JSX.Element | null {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const { push, location } = useHistory();
  const { pathname } = useLocation();

  // 扁平化的menu
  const flatMenu = useMemo(() => flattenMenu(menu), [menu]);

  // 监测location.pathname，通过正则去激活菜单，否则通过link包含来激活
  const activeKey = useMemo(() => {
    const activeItem = flatMenu.find(
      ({ link, activeRegExp }) =>
        (activeRegExp && activeRegExp.test(pathname)) || (link && pathname.includes(link))
    );
    return activeItem?.name || '';
  }, [pathname, flatMenu]);

  // 到达当前激活key的path数组，用以展开菜单
  const openKeys = useMemo(() => {
    const openPath = getPath(menu, activeKey).slice(0, -1);
    const openRootMenu = menu.map(({ name }) => name);

    return Array.from(new Set([...openPath, ...openRootMenu]));
  }, [activeKey, menu]);

  // 点击菜单，进行链接跳转
  const handleLink = (selectKey: string) => {
    const link = flatMenu.find((item) => item.name === selectKey)?.link;

    if (link && link !== pathname) {
      push(link);
    }
  };

  useEffect(() => {
    user.init().then(({ privilegeList }) => {
      const list = filterMenu(MENU, privilegeList || []);
      setMenu(list);

      // 如果当前在根目录则重定向到第一个可跳转的tab
      if (location.pathname === '/') {
        const link = list[0]?.children?.[0].link;
        link && push(link);
      }
    });
  }, [location.pathname, push]);

  return menu.length > 0 ? (
    <StyledMenu>
      <Menu
        mode="inline"
        // 默认第一级菜单全部展开
        defaultOpenKeys={openKeys}
        inlineIndent={15}
        selectedKeys={[activeKey]}
        onClick={({ key }) => handleLink(String(key))}
        style={{ width: '100%' }}
        items={formatMenuItem(menu)}
      />
    </StyledMenu>
  ) : null;
}
